import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  SimpleGrid,
  HStack,
  VStack,
  Icon,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useToast,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import axios from "axios";
import { getToken } from "utils/auth";
import config from "config";
import {
  FaDatabase,
  FaDownload,
  FaUpload,
  FaExclamationTriangle,
  FaServer,
  FaFileArchive,
  FaUsers,
  FaSitemap,
  FaUserInjured,
  FaCheckCircle,
} from "react-icons/fa";

function Backup() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const fileInputRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const filterBg = useColorModeValue("slate.50", "navy.800");

  const fetchStats = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${config.API_URL}/admin/backup/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching database stats:", err);
      toast({
        title: "Error fetching database stats",
        description: err.response?.data?.error || "Could not retrieve live metrics.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleDownloadBackup = async () => {
    setIsDownloading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${config.API_URL}/admin/backup/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Extract filename from header if available
      let filename = `xp_database_backup_${new Date().toISOString().slice(0, 10)}.sql.gz`;
      const disposition = res.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const matches = disposition.match(/filename="?([^";]+)"?/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      // Trigger browser download
      const blob = new Blob([res.data], { type: "application/gzip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Backup Generated",
        description: `Successfully downloaded ${filename}`,
        status: "success",
        duration: 6000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error downloading backup:", err);
      toast({
        title: "Backup Failed",
        description: err.response?.data?.error || "Failed to generate database dump.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const validExts = [".gz", ".sql"];
      const hasValidExt = validExts.some((ext) => file.name.toLowerCase().endsWith(ext));

      if (!hasValidExt) {
        toast({
          title: "Invalid File Format",
          description: "Please upload a valid .sql or .sql.gz PostgreSQL backup file.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleOpenRestoreModal = () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please choose a backup archive first.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setConfirmText("");
    onOpen();
  };

  const handleExecuteRestore = async () => {
    if (confirmText.trim().toUpperCase() !== "RESTORE") {
      return;
    }

    setIsRestoring(true);
    onClose();

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("backup_file", selectedFile);

      const res = await axios.post(`${config.API_URL}/admin/backup/restore`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast({
        title: "Database Restored",
        description: res.data?.message || "Database restoration completed successfully!",
        status: "success",
        duration: 8000,
        isClosable: true,
      });

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh database metrics
      await fetchStats();
    } catch (err) {
      console.error("Error restoring database:", err);
      toast({
        title: "Restore Failed",
        description:
          err.response?.data?.error ||
          "Failed to restore database from archive. Please check server logs.",
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <Flex direction="column" pt={{ base: "62px", md: "75px", xl: "88px" }} gap="24px" w="100%">
      {/* Header Banner */}
      <Box
        p={{ base: "18px", sm: "24px", md: "28px" }}
        bg={cardBg}
        borderRadius="24px"
        border="1px solid"
        borderColor={cardBorder}
        shadow="sm"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          gap="16px"
        >
          <Box>
            <HStack spacing="10px" mb="6px">
              <Icon as={FaDatabase} color="teal.500" boxSize="22px" />
              <Heading size="lg" color={textColor} fontWeight="800" letterSpacing="-0.02em">
                Database Backup & Restore
              </Heading>
              <Badge colorScheme="teal" borderRadius="full" px="10px">
                Admin Utilities
              </Badge>
            </HStack>
            <Text fontSize="sm" color={subtitleColor}>
              Generate complete PostgreSQL database snapshots and restore the database.
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Live Database Metrics Overview */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="20px">
        <Box
          p="20px"
          bg={cardBg}
          borderRadius="20px"
          border="1px solid"
          borderColor={cardBorder}
          shadow="sm"
        >
          <HStack justify="space-between" mb="8px">
            <Text fontSize="xs" fontWeight="700" color="teal.500" textTransform="uppercase">
              Database Size
            </Text>
            <Icon as={FaServer} color="teal.500" boxSize="18px" />
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color={textColor}>
            {loadingStats ? <Spinner size="sm" color="teal.500" /> : stats?.database_size || "—"}
          </Text>
          <Text fontSize="xs" color={subtitleColor} mt="2px">
            PostgreSQL DB: {stats?.database_name || "xp"}
          </Text>
        </Box>

        <Box
          p="20px"
          bg={cardBg}
          borderRadius="20px"
          border="1px solid"
          borderColor={cardBorder}
          shadow="sm"
        >
          <HStack justify="space-between" mb="8px">
            <Text fontSize="xs" fontWeight="700" color="red.500" textTransform="uppercase">
              Total Individuals
            </Text>
            <Icon as={FaUserInjured} color="red.500" boxSize="18px" />
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color={textColor}>
            {loadingStats ? <Spinner size="sm" color="red.500" /> : stats?.counts?.individuals ?? 0}
          </Text>
          <Text fontSize="xs" color={subtitleColor} mt="2px">
            {stats?.counts?.medical_records ?? 0} medical dossiers
          </Text>
        </Box>

        <Box
          p="20px"
          bg={cardBg}
          borderRadius="20px"
          border="1px solid"
          borderColor={cardBorder}
          shadow="sm"
        >
          <HStack justify="space-between" mb="8px">
            <Text fontSize="xs" fontWeight="700" color="cyan.500" textTransform="uppercase">
              Cataloged Families
            </Text>
            <Icon as={FaSitemap} color="cyan.500" boxSize="18px" />
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color={textColor}>
            {loadingStats ? <Spinner size="sm" color="cyan.500" /> : stats?.counts?.families ?? 0}
          </Text>
          <Text fontSize="xs" color={subtitleColor} mt="2px">
            Distinct kindred pedigrees
          </Text>
        </Box>

        <Box
          p="20px"
          bg={cardBg}
          borderRadius="20px"
          border="1px solid"
          borderColor={cardBorder}
          shadow="sm"
        >
          <HStack justify="space-between" mb="8px">
            <Text fontSize="xs" fontWeight="700" color="purple.500" textTransform="uppercase">
              System Accounts
            </Text>
            <Icon as={FaUsers} color="purple.500" boxSize="18px" />
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color={textColor}>
            {loadingStats ? <Spinner size="sm" color="purple.500" /> : stats?.counts?.users ?? 0}
          </Text>
          <Text fontSize="xs" color={subtitleColor} mt="2px">
            Active registered users
          </Text>
        </Box>
      </SimpleGrid>

      {/* Main Action Cards: Backup (Export) & Restore (Import) */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="24px">
        {/* CARD 1: EXPORT / BACKUP */}
        <Box
          p={{ base: "16px", sm: "20px", md: "28px" }}
          bg={cardBg}
          borderRadius="24px"
          border="1px solid"
          borderColor={cardBorder}
          shadow="sm"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box>
            <HStack spacing="10px" mb="12px">
              <Box p="10px" borderRadius="14px" bg="teal.50" color="teal.500">
                <Icon as={FaDownload} boxSize="20px" />
              </Box>
              <Heading size="md" color={textColor} fontWeight="800">
                Download Database Backup
              </Heading>
            </HStack>

            <Text fontSize="sm" color={subtitleColor} mb="16px" lineHeight="1.6">
              Generates a complete, single-file snapshot of the PostgreSQL database including all
              individuals, families, medical records, genetic sequencing, users, and forum discussions.
            </Text>

            <Alert status="info" borderRadius="14px" mb="24px" bg={filterBg} borderColor={cardBorder}>
              <AlertIcon color="teal.500" />
              <Box fontSize="xs" color={textColor}>
                <AlertTitle fontWeight="700">Compressed Gzip Archive (.sql.gz)</AlertTitle>
                <AlertDescription>
                  Backups are streamed directly from `pg_dump` with high-ratio compression. The
                  resulting file can be restored here or via PostgreSQL command-line tools.
                </AlertDescription>
              </Box>
            </Alert>
          </Box>

          <Button
            size="lg"
            colorScheme="teal"
            borderRadius="14px"
            leftIcon={<FaDownload />}
            onClick={handleDownloadBackup}
            isLoading={isDownloading}
            loadingText="Generating Backup Archive..."
            w="100%"
            h="auto"
            minH="48px"
            py={{ base: "12px", md: "16px" }}
            px={{ base: "12px", md: "24px" }}
            fontSize={{ base: "xs", sm: "sm", md: "md" }}
            whiteSpace="normal"
            lineHeight="1.3"
            textAlign="center"
          >
            Download Database Backup (.sql.gz)
          </Button>
        </Box>

        {/* CARD 2: IMPORT / RESTORE */}
        <Box
          p={{ base: "16px", sm: "20px", md: "28px" }}
          bg={cardBg}
          borderRadius="24px"
          border="1px solid"
          borderColor={cardBorder}
          shadow="sm"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box>
            <HStack spacing="10px" mb="12px">
              <Box p="10px" borderRadius="14px" bg="red.50" color="red.500">
                <Icon as={FaUpload} boxSize="20px" />
              </Box>
              <Heading size="md" color={textColor} fontWeight="800">
                Restore Database from Backup
              </Heading>
            </HStack>

            <Text fontSize="sm" color={subtitleColor} mb="16px" lineHeight="1.6">
              Upload a previously generated `.sql` or `.sql.gz` backup archive to overwrite and
              restore database state.
            </Text>

            <Alert status="warning" borderRadius="14px" mb="20px" bg="red.50" borderColor="red.200">
              <AlertIcon color="red.500" />
              <Box fontSize="xs" color="red.800">
                <AlertTitle fontWeight="800">Critical Warning: Data Overwrite</AlertTitle>
                <AlertDescription>
                  Restoring will replace all current tables with the contents of the archive.
                  Ensure you download a fresh backup before proceeding.
                </AlertDescription>
              </Box>
            </Alert>

            {/* File Selector */}
            <Box
              p="16px"
              borderRadius="16px"
              border="2px dashed"
              borderColor={selectedFile ? "teal.400" : cardBorder}
              bg={filterBg}
              textAlign="center"
              cursor="pointer"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              _hover={{ borderColor: "teal.400" }}
              transition="all 0.2s"
              mb="24px"
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".sql,.gz"
                onChange={handleFileChange}
              />
              <Icon as={FaFileArchive} boxSize="28px" color={selectedFile ? "teal.500" : "gray.400"} mb="6px" />
              <Text fontSize="sm" fontWeight="700" color={textColor}>
                {selectedFile ? selectedFile.name : "Click to select backup file (.sql or .sql.gz)"}
              </Text>
              <Text fontSize="2xs" color={subtitleColor} mt="2px">
                {selectedFile
                  ? `${(selectedFile.size / 1024).toFixed(1)} KB — Ready to restore`
                  : "Supports standard and gzip-compressed PostgreSQL dumps up to 200MB"}
              </Text>
            </Box>
          </Box>

          <Button
            size="lg"
            colorScheme="red"
            borderRadius="14px"
            leftIcon={<FaUpload />}
            onClick={handleOpenRestoreModal}
            isDisabled={!selectedFile || isRestoring}
            isLoading={isRestoring}
            loadingText="Restoring Database..."
            w="100%"
            h="auto"
            minH="48px"
            py={{ base: "12px", md: "16px" }}
            px={{ base: "12px", md: "24px" }}
            fontSize={{ base: "xs", sm: "sm", md: "md" }}
            whiteSpace="normal"
            lineHeight="1.3"
            textAlign="center"
          >
            Restore Database from File
          </Button>
        </Box>
      </SimpleGrid>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent borderRadius="20px" bg={cardBg} border="1px solid" borderColor={cardBorder}>
          <ModalHeader color="red.500" display="flex" alignItems="center" gap="10px">
            <Icon as={FaExclamationTriangle} />
            Confirm Database Restoration
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing="14px" align="stretch">
              <Text fontSize="sm" color={textColor}>
                You are about to restore the database from the following backup file:
              </Text>
              <Box p="10px" bg={filterBg} borderRadius="10px" border="1px solid" borderColor={cardBorder}>
                <HStack spacing="8px">
                  <Icon as={FaFileArchive} color="teal.500" />
                  <Text fontSize="xs" fontWeight="700" color={textColor}>
                    {selectedFile?.name}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor}>
                    ({selectedFile ? (selectedFile.size / 1024).toFixed(1) : 0} KB)
                  </Text>
                </HStack>
              </Box>
              <Text fontSize="xs" color="red.500" fontWeight="700">
                This action is irreversible. All current database records not in this backup will be lost.
              </Text>
              <Box>
                <Text fontSize="xs" fontWeight="600" color={textColor} mb="6px">
                  Type <Text as="span" fontWeight="800" color="red.500">RESTORE</Text> to confirm:
                </Text>
                <Input
                  size="sm"
                  borderRadius="10px"
                  placeholder="RESTORE"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap="10px">
            <Button variant="ghost" borderRadius="10px" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              borderRadius="10px"
              leftIcon={<FaCheckCircle />}
              isDisabled={confirmText.trim().toUpperCase() !== "RESTORE"}
              onClick={handleExecuteRestore}
            >
              Confirm & Overwrite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default Backup;
